import { Box } from '@ff-it/ui';
import { ItemLoader, NotFound, UpdateContainer, useEntity, useModel } from 'components';
import { useHasPerm } from 'core/permissions';
import { TermsForm } from 'modules/client/agreement/TermsForm';
import { ClientAgreement, type AgreementTypeTerms } from 'modules/client/agreement/types';
import { ClientDiscountRootsEditor, type ClientAgreementVendorTerms } from 'modules/client/agreement/vendorTerms';
import { type ReactElement } from 'react';
import { useParams } from 'react-router';
import { mutate as globalMutate } from 'swr';
import { ActivityType, activityTypes } from 'types';

type ClientDiscountSectionCollection = {
  type: ActivityType;
  type_terms_exists: boolean;
  vendor_terms: ClientAgreementVendorTerms[];
};

function termsFormValues({ terms, service_rates, smart_rate_groups }: AgreementTypeTerms) {
  // Independently saved discount rules must never enter the terms POST payload.
  return { terms, service_rates, smart_rate_groups };
}

export function Terms(): ReactElement {
  const { type } = useParams<{ type: ActivityType }>();
  if (!type || !activityTypes.includes(type)) {
    return <NotFound />;
  }

  return <TermsContainer key={type} type={type} />;
}

function TermsContainer({ type }: { type: ActivityType }): ReactElement {
  const { permissions } = useModel();
  const { endpoint } = useEntity<ClientAgreement>();
  const hasUpdate = useHasPerm(permissions?.change);
  const url = `${endpoint}type_terms/${type}/`;
  const refreshIndicators = async () => {
    await globalMutate({ method: 'GET', url: endpoint });
  };

  return (
    <>
      <ItemLoader<AgreementTypeTerms> url={url}>
        {({ item, mutate }) => (
          <UpdateContainer
            url={url}
            initialValues={termsFormValues(item)}
            updateItem={async (value) => {
              await mutate(value as AgreementTypeTerms, { revalidate: false });
              await refreshIndicators();
            }}
            method="POST"
            disabled={!hasUpdate}
          >
            <TermsForm type={type} />
          </UpdateContainer>
        )}
      </ItemLoader>
      <ClientDiscountSections type={type} url={url} readOnly={!hasUpdate} refreshIndicators={refreshIndicators} />
    </>
  );
}

function ClientDiscountSections({
  type,
  url,
  readOnly,
  refreshIndicators,
}: {
  type: ActivityType;
  url: string;
  readOnly: boolean;
  refreshIndicators: () => Promise<void>;
}): ReactElement {
  return (
    <ItemLoader<ClientDiscountSectionCollection> url={`${url}vendor-terms/`}>
      {({ item, mutate }) => {
        const refresh = async () => {
          await mutate();
          await refreshIndicators();
        };

        return (
          <div className="container px-3 ml-0 mb-3">
            <section aria-label="Client discount sections" data-testid="client-media-discount-rates">
              <Box fontWeight="semibold" mb="3">
                Client discounts
              </Box>
              <ClientDiscountRootsEditor
                type={type}
                url={url}
                sections={item.vendor_terms}
                readOnly={readOnly}
                refresh={refresh}
              />
            </section>
          </div>
        );
      }}
    </ItemLoader>
  );
}
